import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { filter, finalize, Subject, take, takeUntil } from 'rxjs';
import { ToastsService } from '@tgx/shared/toasts';
import { SignupService } from '../../signup.service';
import { FormValidator } from '@tgx/shared/utils';
import { GtmDataLayerService } from '@tgx/shared/services';
import { CreateUserInput } from '../../models/create-user-input.interface';

interface SignUpUserForm {
  userEmail: FormControl<string>;
  userFirstName: FormControl<string>;
  userLastName: FormControl<string>;
  optedInOfEmailMarketing: FormControl<boolean>;
  doAgreeConditions: FormControl<boolean>;
}

interface FormInputConfig {
  label: string;
  placeholder: string;
  controlName: 'userEmail' | 'userFirstName' | 'userLastName';
  type: 'email' | 'text';
}

interface FormCheckConfig {
  text: string;
  controlName: 'optedInOfEmailMarketing' | 'doAgreeConditions';
}

interface SignUpError {
  errors?: Array<{ level: string; description: string }>;
}

@Component({
  selector: 'tgx-web-site-public-v3-sign-up-user-form',
  templateUrl: './v3-sign-up-user-form.component.html',
  styleUrls: ['./v3-sign-up-user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class V3SignUpUserFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly signupService = inject(SignupService);
  private readonly toastService = inject(ToastsService);
  private readonly gtmService = inject(GtmDataLayerService);

  @Output() protected readonly updateUserEmail = new EventEmitter<string>();

  protected form!: FormGroup<SignUpUserForm>;
  protected readonly formInputs: FormInputConfig[] = [
    {
      label: 'Corporate Email',
      placeholder: 'Your corporate email address',
      controlName: 'userEmail',
      type: 'email',
    },
    {
      label: 'First Name',
      placeholder: 'Your first name',
      controlName: 'userFirstName',
      type: 'text',
    },
    {
      label: 'Last Name',
      placeholder: 'Your last name',
      controlName: 'userLastName',
      type: 'text',
    },
  ];

  protected readonly formChecks: FormCheckConfig[] = [
    {
      text: "Yes! I want to stay in the loop and find out who's joined the Network, unmissable industry events, product news, and much more.",
      controlName: 'optedInOfEmailMarketing',
    },
    {
      text: `By signing up, I agree to Travelgate
      <a href='https://www.travelgate.com/privacy-policy' target='_blank'>Privacy Policy</a>,
      <a href='https://www.travelgate.com/aviso-legal' target='_blank'>Legal Notice</a> and
      <a href='https://www.travelgate.com/cookies-policy' target='_blank'>Cookies Policy</a>`,
      controlName: 'doAgreeConditions',
    },
  ];
  protected isLoading = false;
  private isSubmitted = false;
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    this.form = this.fb.group<SignUpUserForm>({
      userEmail: this.fb.nonNullable.control('', {
        validators: [FormValidator.required('company email'), Validators.email, FormValidator.emailRegex],
        asyncValidators: [FormValidator.validateUser(this.signupService)],
      }),
      userFirstName: this.fb.nonNullable.control('', {
        validators: [
          FormValidator.required('first name'),
          FormValidator.textLength('First name', 3),
          FormValidator.textNumbers('First name'),
          FormValidator.textInvalidChar('First name'),
        ],
      }),
      userLastName: this.fb.nonNullable.control('', {
        validators: [
          FormValidator.required('last name'),
          FormValidator.textLength('Last name', 3),
          FormValidator.textNumbers('Last name'),
          FormValidator.textInvalidChar('Last name'),
        ],
      }),
      optedInOfEmailMarketing: this.fb.nonNullable.control(false),
      doAgreeConditions: this.fb.nonNullable.control(false, {
        validators: [FormValidator.requiredTrue],
      }),
    });
  }

  protected controlInvalid(controlName: keyof SignUpUserForm): boolean {
    return this.isSubmitted && this.form.controls[controlName].invalid;
  }

  protected controlStatus(controlName: keyof SignUpUserForm): string {
    if (!this.isSubmitted) {
      return '';
    }

    return this.form.controls[controlName].invalid ? 'is-invalid' : 'is-valid';
  }

  protected controlError(controlName: keyof SignUpUserForm): string {
    const errorMessage = this.form.controls[controlName].errors?.errorMsg;
    return typeof errorMessage === 'string' ? errorMessage : '';
  }

  protected onSubmit(): void {
    this.isSubmitted = true;

    if (this.form.pending) {
      this.form.statusChanges
        .pipe(
          filter((status) => status !== 'PENDING'),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe(() => this.onSubmit());
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formData = this.form.getRawValue();

    const data: CreateUserInput = {
      userFirstName: formData.userFirstName,
      userLastName: formData.userLastName,
      userEmail: formData.userEmail,
      optedInOfEmailMarketing: formData.optedInOfEmailMarketing,
    };

    this.isLoading = true;
    this.signupService
      .registerUser(data)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.gtmService.customClick();
          this.updateUserEmail.emit(data.userEmail);
        },
        error: (err: SignUpError) => {
          if (Array.isArray(err.errors)) {
            err.errors.forEach((error) => {
              if (error.level === 'ERROR') {
                this.toastService.error(error.description);
              }
            });
            return;
          }

          this.toastService.error('Internal server Error!');
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
